// Import required dependencies for API functions
import axios from '../../../../axiosInstance';
import { toast } from 'react-toastify';
import i18n from 'i18next';

// Tree node search utilities
let nodeFound = false;
let foundNode = null;

/**
 * Recursively searches for a node by ID in the tree structure
 * @param {Object} rootNode - Root node to start search from
 * @param {number} id - ID of the node to find
 */
export const searchForNode = (rootNode, id) => {
  if (rootNode.id === id) {
    foundNode = rootNode;
    nodeFound = true;
    return;
  } else {
    for (let index = 0; index < rootNode.children.length; index++) {
      if (nodeFound) return;
      const child = rootNode.children[index];
      searchForNode(child, id);
    }
  }
};

/**
 * Resets the search state for finding nodes
 */
export const resetSearchingForNode = () => {
  nodeFound = false;
  foundNode = null;
};

/**
 * Returns the node found by the last search operation
 * @returns {Object|null} - Found node or null
 */
export const getFoundNode = () => foundNode;

/**
 * Attaches a new child node to its parent
 * @param {Object} parentNode - Parent node to attach child to
 * @param {Object} newNode - New child node to attach
 */
export const attachChildToParent = (parentNode, newNode) => {
  parentNode.children = [...parentNode.children, newNode];
};

/**
 * Creates a new organizational node with proper hierarchy settings
 * @param {number} nodesCount - Current count of nodes for ID generation
 * @param {Object} parentNode - Parent node to inherit properties from
 * @returns {Object} - New node object
 */
export const createNewNode = (nodesCount, parentNode) => {
  const newNode = {
    id: nodesCount + 1,
    name: `node-${nodesCount + 1}`,
    level: parentNode.level + 1,
    code: `node-${nodesCount + 1}`,
    parent_id: parentNode.id,
    manager: '',
    children: [],
    isNew: true,
  };

  // Set head based on parent level hierarchy
  if (parentNode.level === 1) {
    newNode.head = parentNode.head; // Inherit organization heads
  } else {
    newNode.head = parentNode.manager; // Manager becomes head for child
  }

  return newNode;
};

/**
 * Compares two arrays or values for equality
 * @param {Array|any} arr1 - First array or value to compare
 * @param {Array|any} arr2 - Second array or value to compare
 * @returns {boolean} - True if arrays/values are equal
 */
export const arraysEqual = (arr1, arr2) => {
  if (!Array.isArray(arr1) && !Array.isArray(arr2)) {
    return arr1 === arr2;
  }
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false;
  }
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((val, index) => val === arr2[index]);
};

/**
 * Maps API organization data to internal tree structure
 * @param {Object} apiData - API response containing organization data
 * @returns {Object|null} - Root node of the organizational tree
 */
export const mapApiDataToTree = (
  apiData,
  setGlobalSelectedEmployees,
  setGlobalSelectedManagers,
  setGlobalSelectedHeads
) => {
  const organizationData = apiData.data;
  const departments = organizationData.departments;
  const organizationHeads = organizationData.organizationHeads;

  // Create lookup map for efficient department access
  const departmentMap = new Map();

  // First pass: Create all department nodes
  departments.forEach((dept) => {
    const node = {
      id: dept.id,
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      level: dept.level + 1, // API level 0 becomes tree level 1
      parent_id: dept.parent_department_id,
      children: [],
      // Extract only actual employees (not heads or managers)
      employees: dept.users
        ? dept.users
            .filter(
              (userAssignment) =>
                userAssignment.organizationRole.role_code === 'employee'
            )
            .map((userAssignment) => userAssignment.user.id)
        : [],
      // Extract invited emails from API response
      invitedEmployees: dept.invitedEmployees || [],
    };
    setGlobalSelectedEmployees((prev) => [
      ...prev,
      { node_id: node.id, employees: node.employees },
    ]);
    // Set head and manager based on department level
    if (dept.level === 0) {
      // Root level: organization heads as array
      const orgHeads = dept.users
        ? dept.users
            .filter((userAssignment) => userAssignment.isOrganizationHead)
            .map((userAssignment) => userAssignment.user.id)
        : [];
      node.head =
        orgHeads.length > 0
          ? orgHeads
          : organizationHeads.map((head) => head.id);

      // Extract invited heads for root level
      node.invitedHead = dept.invitedHeads || [];

      // Root level managers (excluding organization heads)
      const managers = dept.users
        ? dept.users
            .filter(
              (userAssignment) =>
                !userAssignment.isOrganizationHead &&
                userAssignment.organizationRole.role_code === 'manager'
            )
            .map((userAssignment) => userAssignment.user.id)
        : [];
      node.manager = managers;

      // Extract invited managers for root level
      node.invitedManager = dept.invitedManagers || [];

      setGlobalSelectedHeads([{ node_id: node.id, heads: node.head }]);
    } else {
      // Child departments: single manager
      const manager = dept.users
        ? dept.users.find(
            (userAssignment) =>
              userAssignment.organizationRole.role_code === 'manager'
          )
        : null;
      node.manager = manager ? manager.user.id : '';

      // Extract invited manager for child departments (single email string)
      node.invitedManager = dept.invitedManager || null;

      // Set head based on manager's reporting structure
      if (manager && manager.reports_to_id) {
        const headUserAssignment = departments
          .flatMap((d) => d.users || [])
          .find((ua) => ua.id === manager.reports_to_id);

        node.head = headUserAssignment ? headUserAssignment.user.id : '';
      } else {
        node.head = '';
      }

      // Extract invited head for level 2 departments (single email string)
      node.invitedHead = dept.invitedHead || null;
    }
    setGlobalSelectedManagers((prev) => [
      ...prev,
      { node_id: node.id, manager: node.manager },
    ]);
    departmentMap.set(dept.id, node);
  });

  // Second pass: Build tree hierarchy
  let rootNode = null;
  departments.forEach((dept) => {
    const currentNode = departmentMap.get(dept.id);

    if (dept.parent_department_id === null) {
      rootNode = currentNode; // This is the root
    } else {
      // Add to parent's children
      const parentNode = departmentMap.get(dept.parent_department_id);
      if (parentNode) {
        parentNode.children.push(currentNode);
      }
    }
  });

  return rootNode;
};

/**
 * Creates API payload for saving department data
 * @param {Object} node - Department node to save
 * @returns {Object} - API payload formatted for department creation/update
 */
export const createDepartmentPayload = (node) => {
  let payload;

  if (node.level === 1) {
    // Root level format
    payload = {
      department: {
        name: node.name,
        description: node.description || '',
      },
      // heads: single value for level 1, wrap in array with {userId: X} for backend
      heads: node.head ? [{ userId: node.head }] : [],
      managers: Array.isArray(node.manager)
        ? node.manager.map((id) => ({ userId: id }))
        : [],
      employees: Array.isArray(node.employees)
        ? node.employees.map((id) => ({ userId: id }))
        : [],
    };

    // Add invited users if present
    if (node.invitedHead) {
      // invitedHead is a single email string for level 1, wrap in array for backend
      payload.invitedHeads = [node.invitedHead];
    }
    if (node.invitedManager && node.invitedManager.length > 0) {
      payload.invitedManagers = node.invitedManager;
    }
    if (node.invitedEmployees && node.invitedEmployees.length > 0) {
      payload.invitedEmployees = node.invitedEmployees;
    }
  } else {
    // Child level format
    payload = {
      department: {
        name: node.name,
        description: node.description || '',
        parent_department_id: node.parent_id || null,
      },
      managerId: node.manager || null, // Single manager for child nodes
      employees: Array.isArray(node.employees)
        ? node.employees.map((id) => ({ userId: id }))
        : [],
    };

    // Add invited manager/employees if present
    if (node.invitedManager) {
      payload.invitedManager = node.invitedManager;
    }
    if (node.invitedEmployees && node.invitedEmployees.length > 0) {
      payload.invitedEmployees = node.invitedEmployees;
    }

    // Add head for level 2 departments
    if (node.level === 2) {
      if (node.head) {
        // headId is the single head for level 2 (inherited from parent)
        payload.headId =
          Array.isArray(node.head) && node.head.length === 1
            ? node.head[0]
            : node.head || null;
      }
      if (node.invitedHead) {
        payload.invitedHead = node.invitedHead;
      }
    }
  }

  return payload;
};

/**
 * Updates child nodes' head property when parent head changes
 * @param {Object} parentNode - Parent node whose children need updating
 * @param {any} newHead - New head value to assign to children
 */
export const updateChildrenHead = (parentNode, newHead) => {
  parentNode.children.forEach((child) => {
    child.head = newHead;
  });
};

// ========== VALIDATION UTILITIES ==========

/**
 * Validates if a parent node can have a child added
 * @param {Object} parentNode - Parent node to validate
 * @returns {Object} - Validation result with isValid and message
 */
export const validateParentForChild = (parentNode) => {
  if (!parentNode.manager) {
    return {
      isValid: false,
      message:
        'Please assign a manager to the parent node before adding a child.',
    };
  }

  return {
    isValid: true,
    message: 'Parent node is valid for child creation',
  };
};

/**
 * Validates node data before saving
 * @param {Object} nodeData - Node data to validate
 * @returns {Object} - Validation result with isValid, errors array, and message
 */
export const validateNodeData = (nodeData) => {
  const errors = [];

  if (!nodeData.name || nodeData.name.trim() === '') {
    errors.push('Department name is required');
  }

  if (nodeData.name && nodeData.name.trim().length < 2) {
    errors.push('Department name must be at least 2 characters long');
  }

  if (nodeData.level > 1 && !nodeData.manager) {
    errors.push('Manager is required for child departments');
  }

  return {
    isValid: errors.length === 0,
    errors,
    message: errors.length === 0 ? 'Node data is valid' : errors.join(', '),
  };
};

// ========== API SERVICE FUNCTIONS ==========

/**
 * Fetches all active users based on organization type
 * @param {string} organizationType - 'internal' or 'external'
 * @param {number|null} organizationId - Organization ID for filtering
 * @param {number|null} categoryId - Category ID for filtering
 * @param {number|null} currentOrganizationId - Current org being edited (to include its users)
 * @returns {Promise<Array>} - Array of active users
 */
export const fetchUsersAPI = async (
  organizationType = 'internal',
  organizationId = null,
  categoryId = null,
  currentOrganizationId = null
) => {
  try {
    let endpoint;

    // build params for the unified available users endpoint
    const typeParam = `type=${organizationType}`;
    const orgParam = organizationId ? `organizationId=${organizationId}` : '';
    const catParam = categoryId ? `categoryId=${categoryId}` : '';
    const currentOrgParam = currentOrganizationId
      ? `currentOrganizationId=${currentOrganizationId}`
      : '';

    const params = [typeParam, orgParam, catParam, currentOrgParam]
      .filter(Boolean)
      .join('&');

    // use unified endpoint for both internal and external orgs
    // - for external: filters by category, excludes users in other external orgs
    // - for internal: excludes users in other internal orgs
    if (organizationType === 'external' && !organizationId && !categoryId) {
      // fallback for external without org/category (legacy behavior)
      endpoint = '/api/endUsers';
    } else {
      endpoint = `/api/endUsers/available?${params}`;
    }

    const response = await axios.get(endpoint);
    const activeUsers = response.data.filter((user) => user.active);
    return activeUsers;
  } catch (error) {
    console.error(`Error fetching ${organizationType} users:`, error);
    toast.error('Failed to load users');
    throw error;
  }
};

/**
 * Fetches organization structure from API and maps it to tree format
 * @param {string|number} organizationId - ID of the organization to fetch
 * @returns {Promise<Object>} - Object containing rootNode, maxId, and maxLevel
 */
export const fetchOrganizationAPI = async (
  organizationId,
  setGlobalSelectedEmployees,
  setGlobalSelectedManagers,
  setGlobalSelectedHeads
) => {
  try {
    const response = await axios.get(
      `/api/organizations/${organizationId}/structure`
    );
    const rootNode = mapApiDataToTree(
      response.data,
      setGlobalSelectedEmployees,
      setGlobalSelectedManagers,
      setGlobalSelectedHeads
    );

    if (rootNode) {
      // Calculate counters based on loaded data
      const departments = response.data.data.departments;
      const maxId = Math.max(...departments.map((dept) => dept.id));
      const maxLevel = Math.max(...departments.map((dept) => dept.level + 1));
      const organizationType = response.data.data.type || 'internal';
      const categoryId = response.data.data.category_id || null;

      return {
        rootNode,
        maxId,
        maxLevel,
        organizationType,
        categoryId,
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching organization:', error);
    toast.error('Failed to load organization structure');
    throw error;
  }
};

/**
 * removes a node from the tree by id and returns the updated tree
 */
export const removeNodeFromTree = (rootNode, nodeId) => {
  if (!rootNode.children) return false;

  const index = rootNode.children.findIndex((child) => child.id === nodeId);
  if (index !== -1) {
    rootNode.children.splice(index, 1);
    return true;
  }

  for (const child of rootNode.children) {
    if (removeNodeFromTree(child, nodeId)) return true;
  }

  return false;
};

/**
 * Largest department id in the tree (for nodesCount after creates).
 */
export const maxDepartmentIdInTree = (rootNode) => {
  let m = rootNode.id;
  (rootNode.children || []).forEach((child) => {
    m = Math.max(m, maxDepartmentIdInTree(child));
  });
  return m;
};

/**
 * Deepest department level in the tree (for lastLevel after creates).
 */
export const maxDepartmentLevelInTree = (rootNode) => {
  let m = rootNode.level;
  (rootNode.children || []).forEach((child) => {
    m = Math.max(m, maxDepartmentLevelInTree(child));
  });
  return m;
};

/**
 * deletes a department via the api
 */
export const deleteDepartmentAPI = async (
  organizationId,
  departmentId,
  replacements = {}
) => {
  try {
    const response = await axios.delete(
      `/api/organizations/${organizationId}/departments/${departmentId}`,
      { data: { replacements } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting department:', error);

    const errorMessage =
      error.response?.data?.message || i18n.t('failed_to_delete_department');

    toast.error(errorMessage, {
      style: { whiteSpace: 'pre-line' },
    });

    throw error;
  }
};

/**
 * Creates a new department via POST
 * @param {Object} node - Department node to create
 * @param {string|number} organizationId - Organization ID
 * @returns {Promise<number>} - Created department ID
 */
export const createDepartmentAPI = async (node, organizationId) => {
  const payload = createDepartmentPayload(node);
  const response = await axios.post(
    `/api/organizations/${organizationId}/departments/with-users`,
    payload
  );
  return response.data.data.results.department.id;
};

/**
 * Updates an existing department via PUT
 * @param {Object} node - Department node to update
 * @param {string|number} organizationId - Organization ID
 * @returns {Promise<number>} - Updated department ID
 */
export const updateDepartmentAPI = async (node, organizationId) => {
  const payload = createDepartmentPayload(node);
  const response = await axios.put(
    `/api/organizations/${organizationId}/departments/${node.id}`,
    payload
  );
  return response.data.data.department.id;
};

/**
 * Saves department data to the API (create or update)
 * @param {Object} node - Department node to save
 * @param {Object} tree - Current tree structure for ID updates
 * @param {string|number} id - Organization ID
 * @returns {Promise<number>} - Department ID from API
 */
export const saveDepartmentAPI = async (node, tree, id) => {
  try {
    if (node.isNew) {
      return await createDepartmentAPI(node, id);
    }
    return await updateDepartmentAPI(node, id);
  } catch (error) {
    console.error('Error saving department:', error);
    throw error;
  }
};
